import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {BaseStoreItem} from '../../../../shared/store/BaseStoreItem'
import {UserLoginState} from '../../../../shared/UserLoginState'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {SidebarTabInterface, UISidebarView} from '../ui_components/sidebar/UISidebarView'
import {createHeaderIn, createImgIn, createLinkIn, setElementInnerHTML} from '../ui_utils/DomUtils'
import {UIProfileLinkController} from './UIProfileLinkController'
import {UIProfileOverviewController} from './UIProfileOverviewController'
import {ColonistMembershipType, LobbyImage} from '@colonist/types'
import {ClientUserState} from '../ClientUserState.ts'

const profileTabsInfo: SidebarTabInterface[] = [
    {text: {key: 'strings:profilePage.overview.title'}, class: 'icon_profile_overview', href: '#overview', imageSrc: LobbyImage.ProfileOverview, id: 'tab-input-overview'},
    {text: {key: 'strings:profilePage.navigationTabs.ranked'}, class: 'icon_profile_ranked', href: '#ranked', imageSrc: LobbyImage.ProfileRanked, id: 'tab-input-ranked'},
    {text: {key: 'strings:profilePage.navigationTabs.history'}, class: 'icon_profile_history', href: '#history', imageSrc: LobbyImage.ProfileHistory, id: 'tab-input-game-history'},
    {text: {key: 'strings:profilePage.navigationTabs.items'}, class: 'icon_profile_items', href: '#items', imageSrc: LobbyImage.ProfileItems, id: 'tab-input-items'},
    {text: {key: 'strings:profilePage.navigationTabs.friends'}, class: 'icon_profile_friends', href: '#friends', imageSrc: LobbyImage.Friends, id: 'tab-input-friends'},
    {text: {key: 'strings:profilePage.navigationTabs.account'}, class: 'icon_profile_settings', href: '#settings', imageSrc: LobbyImage.ProfileSettings, id: 'tab-input-settings'},
]

export abstract class UIProfileController {
    username: string

    readonly profileLinkController: UIProfileLinkController
    private overviewController: UIProfileOverviewController

    constructor() {
        const profileSidebarElement = document.getElementById('profile_sidebar') as HTMLDivElement
        new UISidebarView(profileSidebarElement, profileTabsInfo)

        this.profileLinkController = new UIProfileLinkController()

        ClientUserState.registerChangeListener(() => {
            if(this.username == undefined) return
            if(ClientUserState.userState == undefined) return
            if(ClientUserState.userState.username != this.username) return
            void this.getStickyPlayerInfo()
        })
    }

    protected init() {
        this.setOnClickHandlers()

        const defaultSectionIdx = 0
        const sectionFromHash = this.profileLinkController.showSectionFromHash()
        if(sectionFromHash == false) this.profileLinkController.showSection(defaultSectionIdx)

        void this.makeApiCall()
        void this.getStickyPlayerInfo()

        this.overviewController = new UIProfileOverviewController()
    }

    private setOnClickHandlers() {
        this.profileLinkController.sectionLinks.forEach((link, idx) => {
            link.hasMadeAPICall = false
            link.onclick = (e) => {
                e.preventDefault()
                this.profileLinkController.activeLink = link
                this.profileLinkController.showSection(idx)
                // TODO: Move highlightActiveTab() to showActiveSection() on Web
                void this.makeApiCall()
            }
        })
    }

    abstract getUsername(): string
    abstract setStickyUsername(username: string): void
    abstract setUserIcon(iconImage: LobbyImage | undefined): void
    abstract setMembershipIcon(membershipType: ColonistMembershipType | undefined): void
    abstract accountDoesNotExist(): void
    abstract setHistory(historyData: AjaxDataTypes.ProfileHistoryData): void
    abstract setRankedSeasons(seasons: AjaxDataTypes.ProfileDataRankedSeasonDropdown[]): void
    abstract setProfileSettings(userLogins: UserLoginState[]): void

    // eslint-disable-next-line class-methods-use-this
    setItems(items: BaseStoreItem[]): void {
        const itemsDiv = document.getElementById('profile_items_div') as HTMLDivElement
        setElementInnerHTML(itemsDiv, {key: 'strings:colonistCommon.utils.emptyString'})
        for(const item of items) {
            const container = createLinkIn(itemsDiv, '/store', {key: 'strings:colonistCommon.utils.emptyString'}, '')
            createHeaderIn(container, item.name, '', '', 'H4')
            createImgIn(container, item.image)
        }
    }

    async getStickyPlayerInfo() {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserProfile.stickyPlayerInfo(this.username), undefined)
        if(response == undefined) {
            this.accountDoesNotExist()
            return
        }
        const stickyPlayerInfo = response.json as AjaxDataTypes.PublicProfileDataStickyPlayerInfo
        this.setUserIcon(stickyPlayerInfo.iconImageId)
        this.setMembershipIcon(stickyPlayerInfo.currentMembershipType)
        this.setStickyUsername(stickyPlayerInfo.username)
    }

    async makeApiCall() {
        switch(this.profileLinkController.activeLink.id) {
            case this.profileLinkController.overviewSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.PublicProfileDataOverview>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfile.overview(this.username), (data: AjaxDataTypes.PublicProfileDataOverview) => {
                    this.overviewController.setStats(data)
                })
                break
            case this.profileLinkController.historySectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.ProfileHistoryData>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfile.history(this.username), (data: AjaxDataTypes.ProfileHistoryData) => {
                    this.setHistory(data)
                })
                break
            case this.profileLinkController.itemsSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<BaseStoreItem[]>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfile.items(this.username), (items: BaseStoreItem[]) => {
                    this.setItems(items)
                })
                break
            case this.profileLinkController.rankedSectionLink.id:
                // TODO: Fix this
                // this.rankedController = new UIProfileRankedController(this.username)
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.ProfileDataRankedSeasonDropdown[]>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserProfileRankedSeasons(), (seasons: AjaxDataTypes.ProfileDataRankedSeasonDropdown[]) => {
                    this.setRankedSeasons(seasons)
                })
                break
            case this.profileLinkController.friendsSectionLink.id:
                break
            case this.profileLinkController.settingsSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<UserLoginState[]>(this.profileLinkController.activeLink, UIAPIEndpoints.apiUserLoginState(), (userLogins: UserLoginState[]) => {
                    this.setProfileSettings(userLogins)
                })
                break
            default:
                break
        }
    }
}
